import { DateDiff, WeekSelect } from "@/types";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const getDaySuffix = (day: number): string => {
  if (day >= 11 && day <= 13) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

export const formatDate = (date: Date | string, format?: string): string => {
  const day =
    typeof date === "string" ? new Date(date).getDate() : date.getDate();
  const suffix = getDaySuffix(day);
  return dayjs(date).format(format ?? `D[${suffix}] MMM YYYY, HH:mm`);
};

type TimeDiffFormat = "DDd HHh";

export const formatTimeDiff = (
  date: number, // timestamp
  format: TimeDiffFormat = "DDd HHh"
) => {
  const totalHours = Math.abs(dayjs().diff(dayjs(date * 1000), "hour"));
  const DD = Math.floor(totalHours / 24);
  const HH = totalHours % 24;

  if (format === "DDd HHh") return DD <= 0 ? `${HH}h` : `${DD}d ${HH}h`;
  return date;
};

export const formatTimestamp = (
  timestamp: number,
  format: "short" | "long" | "longer" | string = "long"
) =>
  format === "long"
    ? dayjs(timestamp * 1000).format("DD MMM YY") // "13 Apr 25"
    : format === "short"
      ? dayjs(timestamp * 1000).format("DD MMM") // "05 Dec"
      : dayjs(timestamp * 1000).format(format);

/**
 * @description Finds difference between two dates
 * @param startDate UTC data-time string
 * @param endDate UTC data-time string
 * @returns { days: number, minutes: number, hours: number }
 * @example getDiff('2025-12-01T00:00:00.000Z', '2025-12-01T00:00:00.000Z')
 */
export const getDiff = (startDate: string, endDate: string): DateDiff => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const hoursDiff = end.diff(start, "h");
  const minutesDiff = end.diff(start, "m");
  const secondsDiff = end.diff(start, "s");

  const days = Math.floor(hoursDiff / 24);
  const hours = hoursDiff - days * 24;
  const minutes = minutesDiff - Math.floor(hoursDiff * 60);
  const seconds = secondsDiff - Math.floor(minutesDiff * 60);

  return {
    days,
    hours,
    minutes,
    seconds,
  };
};

export const getDiffFrom = (date: string) => {
  const from = dayjs(date);
  return from.format("DD/MM/YYYY, HH:mm:ss");
};

/**
 * @description Converts a date to its corresponding week
 * number in the calendar year.
 * @param date - UTC data-time string
 * @returns The week number (1-53) in the calendar year
 * @example dateToWeekOfYear('2025-12-01T00:00:00.000Z')
 */
export const dateToWeekOfYear = (date: string) => dayjs(date).week();

export const dateRelative = (date: string) =>
  dayjs().isBefore(dayjs(date))
    ? dayjs().from(dayjs(date), true)
    : dayjs().to(dayjs(date));

export const getTimeUntil = (
  targetDate: number, // timestamp
  { seconds }: { seconds?: boolean } = { seconds: false }
) => {
  const target = dayjs(targetDate * 1000);
  const now = dayjs();
  // Get the difference in milliseconds
  const diff = target.diff(now);
  // Convert to duration object
  const duration = dayjs.duration(diff);
  return {
    days: !seconds ? Math.floor(duration.days()) : undefined,
    hours: duration.hours(),
    minutes: duration.minutes(),
    seconds: seconds ? duration.seconds() : undefined,
  };
};

/**
 * @description Gets next 2 weeks data
 * @returns {WeekSelect} An object containing next 2 weeks objects
 * @example getNext2Weeks()
 */
export const getNext2Weeks = (): WeekSelect => {
  const now = dayjs();
  // start of week A is the next Monday, 03:00 GMT + 3
  const startOfWeekA = now
    .tz("Europe/Athens")
    .startOf("week")
    .add(1, "w")
    .add(1, "day")
    .add(3, "h");
  const endOfWeekA = startOfWeekA.add(7, "d");
  const startOfWeekB = startOfWeekA.add(1, "w");
  const endOfWeekB = startOfWeekB.add(7, "d");
  const weekA = {
    week: startOfWeekA.week(),
    start: startOfWeekA.format("DD MMM"),
    startTimestamp: startOfWeekA.unix(),
    end: endOfWeekA.format("DD MMM"),
    endTimestamp: endOfWeekA.unix(),
  };
  const weekB = {
    week: startOfWeekB.week(),
    start: startOfWeekB.format("DD MMM"),
    startTimestamp: startOfWeekB.unix(),
    end: endOfWeekB.format("DD MMM"),
    endTimestamp: endOfWeekB.unix(),
  };

  return { weekA, weekB };
};

// Checks if a timestamp is within a specified time period from now
export const isWithinTimePeriod = (
  timestamp: number,
  seconds: number = 3600
): boolean => {
  const now = dayjs();
  const date = dayjs(timestamp);
  const diffInSeconds = now.diff(date, "second");

  return diffInSeconds < seconds;
};

// Formats a timestamp to show "X mins ago" if it's within the last hour,
// otherwise shows the date in the given `dateFormat` format
export const formatRecentTimestamp = (
  timestamp: number,
  dateFormat?: string
): string => {
  const now = dayjs();
  const date = dayjs(timestamp);
  const diffInMinutes = now.diff(date, "minute");

  if (isWithinTimePeriod(timestamp, 3600)) {
    return `${diffInMinutes}m ago`;
  } else {
    return date.format(dateFormat ?? "DD/MM/YYYY, HH:mm");
  }
};
